import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
  listLatestUpdates,
  createLatestUpdate,
  updateLatestUpdate,
  toggleLatestUpdate,
  toggleFeatured,
  deleteLatestUpdate,
  normalizeLatestUpdate,
} from './services/latestUpdateService';

const Container = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.5rem;
  justify-content: space-between;
`;

const ToolbarLeft = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  flex: 1;
  min-width: 0;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 160px;
  flex: 1;
  max-width: 280px;
`;

const ToolbarLabel = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.65);
`;

const SearchInput = styled.input`
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.9rem;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.45);
  }
  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.8);
  }
`;

const FilterSelect = styled.select`
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.25);
  color: #fff;
  font-size: 0.9rem;
  min-width: 140px;

  option {
    color: #1a202c;
  }
  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.8);
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const PageTitle = styled.h2`
  color: #fff;
  font-size: 1.35rem;
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.02em;
`;

const Button = styled.button`
  padding: 10px 20px;
  background: rgba(102, 126, 234, 0.95);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #5a67d8;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 8px 14px;
  background: transparent;
  color: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.08);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DangerButton = styled.button`
  padding: 8px 14px;
  background: transparent;
  color: #feb2b2;
  border: 1px solid rgba(254, 178, 178, 0.5);
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(229, 62, 62, 0.25);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Table = styled.table`
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
  background: rgba(0, 0, 0, 0.15);
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 14px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.55);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.2);
`;

const Td = styled.td`
  padding: 12px 14px;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.92);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  vertical-align: middle;
`;

const Tr = styled.tr`
  &:hover td {
    background: rgba(255, 255, 255, 0.04);
  }
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${(p) =>
    p.$active
      ? 'rgba(72, 187, 120, 0.25)'
      : 'rgba(237, 137, 54, 0.25)'};
  color: ${(p) => (p.$active ? '#9ae6b4' : '#fbd38d')};
  border: 1px solid
    ${(p) =>
      p.$active
        ? 'rgba(72, 187, 120, 0.45)'
        : 'rgba(237, 137, 54, 0.45)'};
`;

const FeaturedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: rgba(245, 158, 11, 0.25);
  color: #fcd34d;
  border: 1px solid rgba(245, 158, 11, 0.45);
`;

const TypePill = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${(p) =>
    p.$type === 'pdf'
      ? 'rgba(139, 92, 246, 0.25)'
      : 'rgba(59, 130, 246, 0.25)'};
  color: ${(p) => (p.$type === 'pdf' ? '#c4b5fd' : '#93c5fd')};
  border: 1px solid
    ${(p) =>
      p.$type === 'pdf'
        ? 'rgba(139, 92, 246, 0.45)'
        : 'rgba(59, 130, 246, 0.45)'};
`;

const ActionsCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Message = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  font-weight: 500;

  ${({ $success }) =>
    $success &&
    `
    background: rgba(72, 187, 120, 0.2);
    color: #c6f6d5;
    border: 1px solid rgba(72, 187, 120, 0.35);
  `}
  ${({ $error }) =>
    $error &&
    `
    background: rgba(245, 101, 101, 0.15);
    color: #fed7d7;
    border: 1px solid rgba(245, 101, 101, 0.35);
  `}
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalCard = styled.div`
  background: #f7fafc;
  border-radius: 12px;
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.75rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
`;

const ModalTitle = styled.h3`
  margin: 0 0 1.25rem;
  font-size: 1.2rem;
  color: #1a202c;
  font-weight: 600;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.label`
  font-size: 0.8rem;
  font-weight: 600;
  color: #4a5568;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  font-size: 0.875rem;
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15);
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  font-size: 0.875rem;
  min-height: 150px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15);
  }
`;

const FieldError = styled.span`
  font-size: 0.75rem;
  color: #c53030;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
`;

const GhostButton = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid #cbd5e0;
  background: #fff;
  color: #2d3748;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background: #edf2f7;
  }
`;

const PrimaryModalButton = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: none;
  background: #5a67d8;
  color: #fff;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #4c51bf;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const FileInputWrapper = styled.div`
  position: relative;
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 10px 16px;
  background: #667eea;
  color: white;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: background 0.2s;

  &:hover {
    background: #5a67d8;
  }
`;

const FileInfo = styled.div`
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: #4a5568;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2.5rem 1rem;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.9rem;
`;

const TypeSelect = styled.select`
  padding: 10px 12px;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  font-size: 0.875rem;
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15);
  }
`;

const DEBOUNCE_MS = 320;

function validateForm(values) {
  const errors = {};
  const title = String(values.title || '').trim();
  if (title.length < 2) errors.title = 'Title is required (min 2 characters).';

  const type = values.type || 'text';
  if (type === 'text') {
    const content = String(values.content || '').trim();
    if (content.length < 1) errors.content = 'Content is required for text updates.';
  }

  const displayOrder = values.display_order;
  if (displayOrder === '' || displayOrder === null || displayOrder === undefined) {
    errors.display_order = 'Display order is required.';
  } else {
    const order = Number(displayOrder);
    if (!Number.isFinite(order) || !Number.isInteger(order) || order < 0) {
      errors.display_order = 'Display order must be an integer ≥ 0.';
    }
  }

  return errors;
}

function buildPayload(values) {
  let slug = values.slug;
  if (!slug) {
    slug = values.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  const payload = {
    title: String(values.title || '').trim(),
    slug: slug,
    type: values.type || 'text',
    display_order: Math.max(0, parseInt(values.display_order, 10) || 0),
    is_featured: values.is_featured ?? false,
    is_active: values.is_active ?? true,
  };

  if (values.type === 'text') {
    payload.content = String(values.content || '').trim();
  }

  if (values.published_at) {
    payload.published_at = values.published_at;
  }

  return payload;
}

function buildEditSnapshot(row) {
  return {
    title: String(row.title ?? '').trim(),
    slug: String(row.slug ?? '').trim(),
    type: row.type || 'text',
    content: row.content || '',
    display_order: Math.max(0, parseInt(row.display_order, 10) || 0),
    is_featured: row.is_featured || false,
    is_active: row.is_active !== false,
    published_at: row.published_at,
  };
}

function buildPartialUpdate(current, baseline) {
  if (!baseline) return buildPayload(current);
  const patch = {};
  const s = (v) => String(v ?? '').trim();

  if (s(current.title) !== s(baseline.title)) patch.title = s(current.title);
  if (s(current.slug) !== s(baseline.slug)) patch.slug = s(current.slug);
  if (current.type !== baseline.type) patch.type = current.type;
  if (current.type === 'text' && s(current.content) !== s(baseline.content)) {
    patch.content = s(current.content);
  }

  const cOrder = Math.max(0, parseInt(current.display_order, 10) || 0);
  const bOrder = Math.max(0, parseInt(baseline.display_order, 10) || 0);
  if (cOrder !== bOrder) patch.display_order = cOrder;

  if (current.is_featured !== baseline.is_featured) patch.is_featured = current.is_featured;
  if (current.is_active !== baseline.is_active) patch.is_active = current.is_active;
  if (current.published_at !== baseline.published_at) patch.published_at = current.published_at;

  return patch;
}

const defaultForm = () => ({
  title: '',
  slug: '',
  type: 'text',
  content: '',
  display_order: 0,
  is_featured: false,
  is_active: true,
  published_at: '',
});

function LatestUpdateManagement({ showMessage }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [formModal, setFormModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formValues, setFormValues] = useState(defaultForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState({ text: '', type: '' });
  const [pdfFile, setPdfFile] = useState(null);
  const searchTimer = useRef(null);
  const editBaselineRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const data = await listLatestUpdates();
      setRows(data.map(normalizeLatestUpdate));
    } catch (e) {
      setListError(e.message || 'Could not load updates.');
      setRows([]);
      if (e.status === 401 && showMessage) {
        showMessage('Session expired or unauthorized. Please sign in again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim().toLowerCase());
    }, DEBOUNCE_MS);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchInput]);

  const filteredRows = useMemo(() => {
    let list = rows;

    if (statusFilter === 'active') {
      list = list.filter((r) => r.is_active);
    } else if (statusFilter === 'inactive') {
      list = list.filter((r) => !r.is_active);
    }

    if (typeFilter === 'text') {
      list = list.filter((r) => r.type === 'text');
    } else if (typeFilter === 'pdf') {
      list = list.filter((r) => r.type === 'pdf');
    }

    if (!debouncedSearch) return list;

    return list.filter((r) => {
      const hay = [r.title, r.slug].join(' ').toLowerCase();
      return hay.includes(debouncedSearch);
    });
  }, [rows, statusFilter, typeFilter, debouncedSearch]);

  const openCreate = () => {
    editBaselineRef.current = null;
    setFormValues(defaultForm());
    setFieldErrors({});
    setPdfFile(null);
    setFormModal({ mode: 'create' });
  };

  const openEdit = (row) => {
    editBaselineRef.current = buildEditSnapshot(row);
    setFormValues(buildEditSnapshot(row));
    setFieldErrors({});
    setPdfFile(null);
    setFormModal({ mode: 'edit', id: row.id });
  };

  const closeForm = () => {
    editBaselineRef.current = null;
    setFormModal(null);
    setFieldErrors({});
    setFormValues(defaultForm());
    setPdfFile(null);
  };

  const setBannerMsg = (text, type = 'success') => {
    setBanner({ text, type });
    setTimeout(() => setBanner({ text: '', type: '' }), 4000);
  };

  const onSubmitForm = async (e) => {
    e.preventDefault();
    setFieldErrors({});

    const initialErrors = validateForm(formValues);
    if (Object.keys(initialErrors).length) {
      setFieldErrors(initialErrors);
      return;
    }

    // Validate PDF file if PDF type is selected
    if (formValues.type === 'pdf' && !pdfFile && formModal.mode === 'create') {
      setFieldErrors({ pdf: 'PDF file is required for PDF updates.' });
      return;
    }

    setSubmitting(true);
    try {
      if (formModal.mode === 'edit') {
        const baseline = editBaselineRef.current;
        const patch = buildPartialUpdate(formValues, baseline);
        if (Object.keys(patch).length === 0 && !pdfFile) {
          setFieldErrors({ _form: 'No changes to save.' });
          return;
        }
        await updateLatestUpdate(formModal.id, patch, pdfFile);
        setBannerMsg('Update updated.', 'success');
        if (showMessage) showMessage('Update updated successfully.', 'success');
      } else {
        const payload = buildPayload(formValues);
        await createLatestUpdate(payload, pdfFile);
        setBannerMsg('Update created.', 'success');
        if (showMessage) showMessage('Update created successfully.', 'success');
      }
      closeForm();
      await load();
    } catch (err) {
      const msg = err.message || 'Request failed.';
      setFieldErrors({ _form: msg });
      if (showMessage) showMessage(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const onToggle = async (row) => {
    const prev = rows.slice();
    const nextActive = !row.is_active;
    setRows((p) =>
      p.map((r) => (r.id === row.id ? { ...r, is_active: nextActive } : r))
    );
    try {
      const raw = await toggleLatestUpdate(row.id, nextActive);
      const updated = normalizeLatestUpdate(raw);
      if (updated) {
        setRows((p) => p.map((r) => (r.id === row.id ? { ...r, ...updated } : r)));
      } else {
        await load();
      }
      if (showMessage) showMessage('Status updated.', 'success');
    } catch (err) {
      setRows(prev);
      const msg = err.message || 'Toggle failed.';
      setBannerMsg(msg, 'error');
      if (showMessage) showMessage(msg, 'error');
    }
  };

  const onToggleFeatured = async (row) => {
    const prev = rows.slice();
    const nextFeatured = !row.is_featured;
    setRows((p) =>
      p.map((r) => (r.id === row.id ? { ...r, is_featured: nextFeatured } : r))
    );
    try {
      const raw = await toggleFeatured(row.id, nextFeatured);
      const updated = normalizeLatestUpdate(raw);
      if (updated) {
        setRows((p) => p.map((r) => (r.id === row.id ? { ...r, ...updated } : r)));
      } else {
        await load();
      }
      if (showMessage) showMessage('Featured status updated.', 'success');
    } catch (err) {
      setRows(prev);
      const msg = err.message || 'Toggle featured failed.';
      setBannerMsg(msg, 'error');
      if (showMessage) showMessage(msg, 'error');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await deleteLatestUpdate(deleteTarget.id);
      setDeleteTarget(null);
      if (showMessage) showMessage('Update removed.', 'success');
      await load();
    } catch (err) {
      const msg = err.message || 'Delete failed.';
      if (showMessage) showMessage(msg, 'error');
      setBannerMsg(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const formMode = formModal?.mode;

  return (
    <Container>
      <TitleRow>
        <PageTitle>Latest Updates Management</PageTitle>
        <Button type="button" onClick={openCreate}>
          Add Update
        </Button>
      </TitleRow>

      {banner.text && (
        <Message $success={banner.type === 'success'} $error={banner.type === 'error'}>
          {banner.text}
        </Message>
      )}

      <Toolbar>
        <ToolbarLeft>
          <Field>
            <ToolbarLabel htmlFor="update-search">Search</ToolbarLabel>
            <SearchInput
              id="update-search"
              placeholder="Title, slug"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoComplete="off"
            />
          </Field>
          <Field>
            <ToolbarLabel htmlFor="update-status-filter">Status</ToolbarLabel>
            <FilterSelect
              id="update-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </FilterSelect>
          </Field>
          <Field>
            <ToolbarLabel htmlFor="update-type-filter">Type</ToolbarLabel>
            <FilterSelect
              id="update-type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="text">Text</option>
              <option value="pdf">PDF</option>
            </FilterSelect>
          </Field>
        </ToolbarLeft>
      </Toolbar>

      {listError && (
        <Message $error>
          {listError}
        </Message>
      )}

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Title</Th>
              <Th>Slug</Th>
              <Th>Type</Th>
              <Th>Order</Th>
              <th>Featured</th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <Tr>
                <Td colSpan={7}>
                  <EmptyState>Loading…</EmptyState>
                </Td>
              </Tr>
            ) : filteredRows.length === 0 ? (
              <Tr>
                <Td colSpan={7}>
                  <EmptyState>No updates match the current filters.</EmptyState>
                </Td>
              </Tr>
            ) : (
              filteredRows.map((row) => (
                <Tr key={row.id}>
                  <Td>{row.title}</Td>
                  <Td>{row.slug}</Td>
                  <Td>
                    <TypePill $type={row.type}>{row.type}</TypePill>
                  </Td>
                  <Td>{row.display_order}</Td>
                  <Td>
                    {row.is_featured ? (
                      <FeaturedBadge>★ Featured</FeaturedBadge>
                    ) : (
                      <span style={{ color: 'rgba(255,255,255,0.4)' }}>—</span>
                    )}
                  </Td>
                  <Td>
                    <StatusPill $active={row.is_active}>
                      {row.is_active ? 'Active' : 'Inactive'}
                    </StatusPill>
                  </Td>
                  <Td>
                    <ActionsCell>
                      <SecondaryButton
                        type="button"
                        onClick={() => openEdit(row)}
                      >
                        Edit
                      </SecondaryButton>
                      <SecondaryButton
                        type="button"
                        onClick={() => onToggleFeatured(row)}
                        disabled={submitting}
                      >
                        {row.is_featured ? 'Unfeature' : 'Feature'}
                      </SecondaryButton>
                      <SecondaryButton
                        type="button"
                        onClick={() => onToggle(row)}
                        disabled={submitting}
                      >
                        {row.is_active ? 'Deactivate' : 'Activate'}
                      </SecondaryButton>
                      <DangerButton
                        type="button"
                        onClick={() => setDeleteTarget(row)}
                      >
                        Delete
                      </DangerButton>
                    </ActionsCell>
                  </Td>
                </Tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrap>

      {formModal && (
        <Modal
          onClick={closeForm}
          role="presentation"
        >
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {formMode === 'create' ? 'Add Update' : 'Edit Update'}
            </ModalTitle>
            {fieldErrors._form && <FieldError style={{ display: 'block', marginBottom: 12 }}>{fieldErrors._form}</FieldError>}
            <form onSubmit={onSubmitForm}>
              <FormGrid>
                <FormGroup>
                  <Label htmlFor="f-title">Title *</Label>
                  <Input
                    id="f-title"
                    value={formValues.title}
                    onChange={(e) => setFormValues((v) => ({ ...v, title: e.target.value }))}
                    autoComplete="off"
                  />
                  {fieldErrors.title && <FieldError>{fieldErrors.title}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="f-slug">Slug (optional)</Label>
                  <Input
                    id="f-slug"
                    value={formValues.slug}
                    onChange={(e) => setFormValues((v) => ({ ...v, slug: e.target.value }))}
                    placeholder="Auto-generated from title if empty"
                    autoComplete="off"
                  />
                  {fieldErrors.slug && <FieldError>{fieldErrors.slug}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="f-type">Type *</Label>
                  <TypeSelect
                    id="f-type"
                    value={formValues.type}
                    onChange={(e) => setFormValues((v) => ({ ...v, type: e.target.value }))}
                  >
                    <option value="text">Text (HTML Content)</option>
                    <option value="pdf">PDF Document</option>
                  </TypeSelect>
                </FormGroup>

                {formValues.type === 'text' ? (
                  <FormGroup>
                    <Label htmlFor="f-content">Content *</Label>
                    <TextArea
                      id="f-content"
                      value={formValues.content}
                      onChange={(e) => setFormValues((v) => ({ ...v, content: e.target.value }))}
                      placeholder="HTML content for the update"
                    />
                    {fieldErrors.content && <FieldError>{fieldErrors.content}</FieldError>}
                  </FormGroup>
                ) : (
                  <FormGroup>
                    <Label>Upload PDF {formMode === 'edit' ? '(optional - leave empty to keep existing)' : '*'}</Label>
                    <FileInputWrapper>
                      <FileInput
                        id="f-pdf-file"
                        type="file"
                        accept="application/pdf"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 20 * 1024 * 1024) {
                              setFieldErrors({ pdf: 'PDF file size must be less than 20MB' });
                              return;
                            }
                            setPdfFile(file);
                            setFieldErrors((prev) => ({ ...prev, pdf: null }));
                          }
                        }}
                      />
                      <FileInputLabel htmlFor="f-pdf-file">
                        Choose PDF File
                      </FileInputLabel>
                    </FileInputWrapper>
                    {pdfFile && (
                      <FileInfo>
                        Selected: {pdfFile.name} ({(pdfFile.size / (1024 * 1024)).toFixed(2)} MB)
                      </FileInfo>
                    )}
                    {fieldErrors.pdf && <FieldError>{fieldErrors.pdf}</FieldError>}
                    {formMode === 'edit' && !pdfFile && (
                      <FileInfo style={{ color: '#718096' }}>
                        Current: {(formModal.id && rows.find(r => r.id === formModal.id)?.pdf_file_name) || 'No PDF'}
                      </FileInfo>
                    )}
                  </FormGroup>
                )}

                <FormGroup>
                  <Label htmlFor="f-display-order">Display Order *</Label>
                  <Input
                    id="f-display-order"
                    type="number"
                    value={formValues.display_order}
                    onChange={(e) => setFormValues((v) => ({ ...v, display_order: e.target.value }))}
                    min="0"
                  />
                  {fieldErrors.display_order && <FieldError>{fieldErrors.display_order}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="f-featured">Featured</Label>
                  <select
                    id="f-featured"
                    value={formValues.is_featured ? 'true' : 'false'}
                    onChange={(e) => setFormValues((v) => ({ ...v, is_featured: e.target.value === 'true' }))}
                    style={{
                      padding: '10px 12px',
                      border: '1px solid #cbd5e0',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      width: '100%',
                    }}
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="f-active">Active</Label>
                  <select
                    id="f-active"
                    value={formValues.is_active ? 'true' : 'false'}
                    onChange={(e) => setFormValues((v) => ({ ...v, is_active: e.target.value === 'true' }))}
                    style={{
                      padding: '10px 12px',
                      border: '1px solid #cbd5e0',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      width: '100%',
                    }}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="f-published-at">Published At (optional)</Label>
                  <Input
                    id="f-published-at"
                    type="datetime-local"
                    value={formValues.published_at}
                    onChange={(e) => setFormValues((v) => ({ ...v, published_at: e.target.value }))}
                  />
                </FormGroup>
              </FormGrid>

              <ButtonRow>
                <GhostButton type="button" onClick={closeForm}>
                  Cancel
                </GhostButton>
                <PrimaryModalButton type="submit" disabled={submitting}>
                  {submitting ? 'Saving...' : formMode === 'create' ? 'Create Update' : 'Update Update'}
                </PrimaryModalButton>
              </ButtonRow>
            </form>
          </ModalCard>
        </Modal>
      )}

      {deleteTarget && (
        <Modal onClick={() => setDeleteTarget(null)} role="presentation">
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Confirm Delete</ModalTitle>
            <p style={{ color: '#4a5568', marginBottom: '1.5rem' }}>
              Are you sure you want to delete "{deleteTarget.title}"? This will deactivate the update.
            </p>
            <ButtonRow>
              <GhostButton onClick={() => setDeleteTarget(null)}>
                Cancel
              </GhostButton>
              <DangerButton onClick={confirmDelete} disabled={submitting}>
                {submitting ? 'Deleting...' : 'Delete'}
              </DangerButton>
            </ButtonRow>
          </ModalCard>
        </Modal>
      )}
    </Container>
  );
}

export default LatestUpdateManagement;
